import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Avatar,
  Box,
  Cards,
  getColor,
  Icons,
  InputField,
  Typography,
  useComponentVisible,
} from "fds";
import "./index.scss";
import useWorkgroupStore from "../../redux/dispatcher/useWorkgroupStore";
import { Member } from "../../types/workgroup";
import utilsCommon from "../../shared/utils/common";
import classNames from "classnames";
import { t } from "i18next";
import { debounce } from "lodash";
import utilsClient from "../../shared/utils/client";
import { Property } from "csstype";
import useProgressStore from "../../redux/dispatcher/useProgressStore";
interface UserSearchFieldProps {
  width: string | number;
  invisibleUserIds?: string[];
  workgroupId?: string;
  type?: "add" | "search";
  clientSize?: { width: number; height: number };
  size?: "sm" | "md" | "lg" | "search";
  hasFrontIcon?: boolean;
  dropdownWidth?: string | number;
  dropdownHeight?: number;
  classes?: string;
  isAllowNewUser?: boolean;
  disabled?: boolean;
  position?: Property.Position;
  onClick?: (user: Member) => void;
}
const UserSearchField: React.FC<UserSearchFieldProps> = ({
  width,
  invisibleUserIds,
  type = "add",
  clientSize,
  hasFrontIcon,
  size = "md",
  classes,
  position = "fixed",
  disabled,
  isAllowNewUser = true,
  workgroupId,
  dropdownWidth,
  dropdownHeight = 195,
  onClick,
}) => {
  const workgroupListNewCard = useRef([] as any[]);
  const { startProgress, stopProgress } = useProgressStore();
  const { workgroupState, getMemberList } = useWorkgroupStore();
  const { ref, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(false);
  const [isInvalidEmail, changeIsInvalidEmail] = useState(false);
  const [inputUserId, changeInputUserId] = useState("");
  const [memeberSearchReq, changeMemberSeacrchReq] = useState({
    searchOpt: "",
    internalPos: 1,
    externalPos: 1,
  });
  const [matchedUserList, changeMatchedUserList] = useState<Member[]>([]);
  const [key, setKey] = useState(0);
  const timeRef = useRef(false);
  useEffect(() => {
    setIsComponentVisible(!!inputUserId);
    !workgroupId && debouncedFetch(inputUserId);
  }, [inputUserId]);
  useEffect(() => {
    if (memeberSearchReq.searchOpt === "") {
      timeRef.current = false;
      stopProgress();
      return;
    }
    if (memeberSearchReq.searchOpt !== "" && !workgroupId) {
      let searchTimer = setTimeout(async () => {
        timeRef.current = false;
        await getMemberList(memeberSearchReq);
      }, 1 * 1000);
      return () => clearTimeout(searchTimer);
    }
  }, [memeberSearchReq]);
  useEffect(() => {
    if (type !== "add") return;
    if (timeRef.current) {
      startProgress();
    } else {
      stopProgress();
    }
  }, [timeRef.current]);
  useEffect(() => {
    let newDataCardContent = [] as any[];
    const totalMember = workgroupId
      ? workgroupState?.workgroupDetail?.workgroup?.members
      : workgroupState?.searchUserResult?.userList;
    if (
      type === "add" &&
      workgroupState?.searchUserResult.searchOpt !== inputUserId
    ) {
      workgroupListNewCard.current = [];
      return;
    }

    // 추가용 field일 경우, 기존에 없던 사용자는 외부 미가입 사용자로 추가 가능
    if (
      totalMember?.filter(
        (item: Member) =>
          [item.userId, item.email, item.userName].includes(inputUserId) &&
          !invisibleUserIds?.includes(item.userId)
      ).length === 0 &&
      !invisibleUserIds?.includes(inputUserId) &&
      type === "add"
    ) {
      newDataCardContent.push(renderMemberItem());
    }
    changeMatchedUserList(
      totalMember?.filter(
        (item: Member) =>
          [item?.userId, item?.email, item?.userName].includes(inputUserId) &&
          !invisibleUserIds?.includes(item.userId)
      )
    );

    totalMember
      ?.filter(
        (item1: Member) =>
          (!workgroupId ||
            item1.userId.includes(inputUserId) ||
            (item1.email && item1.email.includes(inputUserId)) ||
            (item1.userName && item1.userName.includes(inputUserId))) &&
          !invisibleUserIds?.includes(item1.userId)
      )
      ?.forEach((item: Member) =>
        newDataCardContent.push(renderMemberItem(item))
      );
    workgroupListNewCard.current = newDataCardContent;
    setKey(key + 1);
  }, [workgroupState.searchUserResult, inputUserId, workgroupId]);
  useEffect(() => {
    if (!clientSize) return;
    utilsClient.setClientHeight(
      isInvalidEmail ? clientSize.height + 26 : clientSize.height
    );
  }, [isInvalidEmail]);
  useEffect(() => {
    if (!isComponentVisible) {
      stopProgress();
    }
  }, [isComponentVisible]);
  const debouncedFetch = useCallback(
    debounce((val: string) => {
      changeMemberSeacrchReq({
        searchOpt: val,
        internalPos: 1,
        externalPos: 1,
      });
    }, 50),
    []
  );
  const [isComposing, changeIsComping] = useState(false);
  const handleSubmitUser = (user?: Member) => {
    if (!onClick) return;
    const validUser = matchedUserList.filter(
      (item) => !invisibleUserIds?.includes(item.userId)
    );
    if (
      type === "add" &&
      workgroupState?.searchUserResult.searchOpt !== inputUserId
    ) {
      return;
    }
    if (user) {
      onClick(user);
      changeIsInvalidEmail(false);
    } else if (matchedUserList.length > 0) {
      validUser.length > 0 && onClick(validUser[0]);
      changeIsInvalidEmail(false);
    } else if (
      utilsCommon.emailValidation(inputUserId) &&
      !invisibleUserIds?.includes(inputUserId)
    ) {
      onClick({
        status: 2,
        userId: inputUserId,
        userName: null,
        userType: 2,
      });
      changeIsInvalidEmail(false);
    } else if (!utilsCommon.emailValidation(inputUserId)) {
      changeIsInvalidEmail(true);
    }
    changeInputUserId("");
    setIsComponentVisible(false);
    changeIsComping(false);
  };
  const renderMemberItem = (user?: Member) => {
    return {
      id: user ? user.userId : inputUserId,
      data: (
        <Box
          alignItems="center"
          classes="userSearchField-user"
          onClick={() => {
            handleSubmitUser(user);
          }}
        >
          <Avatar
            uniqueCode={utilsCommon.getUserColor(
              user?.userType,
              user?.status,
              user?.userName
            )}
            name={user?.userName ?? user?.userId}
            size="32"
            icon={user && user?.userName ? undefined : "user"}
          />
          <Typography type="body2_rg" classes="mg ml-8" ellipsis>
            {user
              ? utilsCommon.getUserNameIdTag(user.userId, user.userName)
              : inputUserId}
          </Typography>
        </Box>
      ),
    };
  };

  return (
    <Box width={width} classes="userSearchField" ref={ref}>
      <InputField
        placeholder={
          size === "md"
            ? t("common.userInputPlaceholderLong")
            : t("common.userInputPlaceholder")
        }
        size={size}
        leadingIcon={
          hasFrontIcon ? (
            <Icons
              variant="line"
              label="search"
              stroke={
                disabled
                  ? getColor("COLOR_ICON_INTERACTIVE_PRIMARY_DISABLED")
                  : undefined
              }
            />
          ) : undefined
        }
        width={"100%"}
        disabled={disabled}
        value={inputUserId}
        onClickInputField={() => setIsComponentVisible(true)}
        onChange={(e) => {
          timeRef.current = true;
          if (isComposing && e.nativeEvent.isComposing == false) {
            return;
          }
          changeInputUserId(e.target.value);
          changeIsComping(e.nativeEvent.isComposing);
        }}
        onKeyUp={(e) => {
          if (e.code === "Backspace" || e.code.includes("Digit")) {
            changeInputUserId(e.target.value);
            changeIsComping(false);
          } else if (e.code.includes("Enter") && isAllowNewUser) {
            handleSubmitUser();
          }
        }}
        maxLength={64}
        isError={isInvalidEmail}
        helpMessage={isInvalidEmail ? t("common.alert.noValidEmailType") : ""}
        classes={classNames(classes)}
      />
      {isComponentVisible && !!inputUserId && (
        <Box
          classes={classNames(
            "userSearchField-recommend elev sd-300",
            `userSearchField-${size}`
          )}
          style={{ position: position }}
          width={dropdownWidth ?? "329px"}
        >
          {workgroupListNewCard.current.length === 0 ? (
            <Box alignItems="center" justifyContent="center" width={"100%"}>
              <Typography type="body2_rg">
                {t("common.noSearchResult")}
              </Typography>
            </Box>
          ) : (
            <Cards
              size={"md"}
              height={
                Math.min(
                  dropdownHeight,
                  workgroupListNewCard.current.length * 53
                ) + "px"
              }
              backgroundColorType="white"
              width={dropdownWidth ? "100%" : "321px"}
              data={workgroupListNewCard.current}
              endReached={() => {
                changeMemberSeacrchReq({
                  searchOpt: inputUserId,
                  internalPos:
                    workgroupState.searchUserResult.internalPos === -1
                      ? -1
                      : workgroupState.searchUserResult.internalPos + 1,
                  externalPos:
                    workgroupState.searchUserResult.externalPos === -1
                      ? -1
                      : workgroupState.searchUserResult.externalPos + 1,
                });
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
};
export default UserSearchField;
